import React, { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, SearchBar, Radio, Text, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';
const useStyles = createUseStyles(styles, 'SectionToolbarSearch');

function SectionToolbarSearch({
    value,
    className,
    loadOptions,
    onChange,
    onBlurSearch,
    onChangeType,
    isTypeRadio,
    type,
    customOption = null,
    typeOptions,
    ...props
}) {
    const theme = useTheme();
    const classes = useStyles();
    const { t } = useWeb5Context();
    const classNames = useMemo(
        () =>
            classnames(
                {
                    [classes?.SectionToolbarSearch]: !typeOptions,
                    [classes?.withOptions]: typeOptions,
                },
                className,
            ),
        [className, typeOptions, classes],
    );

    const customTypeOption = useCallback(
        (item) => {
            if (!onChangeType || !isTypeRadio) return;
            return (
                <div className={classes.typeOptionRow}>
                    <div className={classes.typeOptionRowRadio}>
                        <Radio
                            checked={item.value === type?.value}
                            overrides={{ svg: { viewBox: '0 0 24 24', width: 18, height: 18 } }}
                        />
                    </div>
                    <div className={classes.typeOptionRowMainInfo}>
                        <Text className={classes.typeOptionRowTitle} type="subtitle" isTruncated>
                            {item.label}
                        </Text>
                        {item.label2 && (
                            <Fragment>
                                <Text
                                    className={classes.typeOptionRowInfo}
                                    type="caption"
                                    color={theme.colors.textLight.secondary}
                                >
                                    {item.label2}
                                </Text>
                                ,
                            </Fragment>
                        )}
                    </div>
                </div>
            );
        },
        [
            classes.typeOptionRow,
            classes.typeOptionRowInfo,
            classes.typeOptionRowMainInfo,
            classes.typeOptionRowRadio,
            classes.typeOptionRowTitle,
            isTypeRadio,
            onChangeType,
            type?.value,
            theme,
        ],
    );

    const overrides = useMemo(() => {
        if (!onChangeType) return;
        return {
            TypeSelector: {
                isRequired: true,
                hideSelectedOptions: false,
                hideDropdownIndicator: false,
                cacheOptions: false,
                dropdownWidth: '320px',
                overrides: {
                    inputComponents: { style: { maxWidth: '150px' } },
                },
            },
        };
    }, [onChangeType]);

    return (
        <SearchBar
            className={classNames}
            placeholder={t('cfm_placeholder_search')}
            size="small"
            highlightMatch
            forceBlurOnEnter
            focusDefaultOption={false}
            hideDropdownIndicator={false}
            value={value}
            loadOptions={loadOptions}
            onChange={onChange}
            onBlurSearch={onBlurSearch}
            type={type}
            onChangeType={onChangeType}
            customTypeOption={customTypeOption}
            customOption={customOption}
            overrides={overrides}
            typeOptions={typeOptions}
            notSelectingDefaultOption
            keepInputFocused
            {...props}
        />
    );
}

SectionToolbarSearch.propTypes = {
    value: PropTypes.object.isRequired,
    loadOptions: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlurSearch: PropTypes.func.isRequired,
};

export default React.memo(SectionToolbarSearch);
